import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { TableHeaderCellProps } from "../props";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedReaction,
  runOnJS,
  measure,
  AnimatedRef,
  useAnimatedRef,
} from "react-native-reanimated";
import { Gesture, GestureDetector, Pressable } from "react-native-gesture-handler";
import { useStorage } from "@/hooks/useStorage";
import useContextMenu from "@/hooks/useContextMenu";

export const TableHeaderCell = forwardRef<AnimatedRef<View>, TableHeaderCellProps>(
  (
    {
      text,
      columnKey,
      columnIndex,
      relativeColumnIndex,
      anchoredCount,
      scrollableCount,
      onResize,
      onResizeEnd,
      columnClipping,
      columnWidths,
      style,
      resizeRealtime,
      anchored,
      setAnchorFields,
      columnPositionX,
      minCellWidth,
      loading,
      storageKey,
      onResizeStart,
      onMoveLeft,
      onMoveRight,
      orderby,
      onOrderByChange,
      onHideColumn,
      onDragStart,
      onDragMove,
      onDragEnd,
      ...rest
    },
    ref,
  ) => {
    const { colors } = useTheme();
    const innerRef = useAnimatedRef<View>();
    useImperativeHandle(ref, () => innerRef, [innerRef]);
    const contextMenu = useContextMenu();
    const [_, setMenuVisible] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [resizerVisible, setResizerVisible] = useState(false); //TODO : consider non-realtime resizer
    const [isDragging, setIsDragging] = useState(false);
    const [measured, setMeasured] = useState<{ x: number; y: number; width: number; height: number }>({
      x: 100,
      y: 100,
      width: 100,
      height: 100,
    });
    const buttonRef = useRef<View>(null);
    const storage = useStorage();
    const savedWidth = storage.getNumber(`${storageKey}-${columnKey}`);
    const width = useSharedValue(savedWidth ?? 200);
    columnWidths[columnKey] = width;
    const [beginUnclip, setBeginUnclip] = useState(false);
    const clippingShared = useSharedValue(false);
    // contextMenu.setVisible(menuVisible);
    const toggleAnchor = () => {
      contextMenu.setVisible(false);
      setAnchorFields((prev) =>
        prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey],
      );
    };

    useAnimatedReaction(
      () => Object.values(columnClipping).some((row) => row[columnKey]) ?? false,
      (isClipping) => {
        clippingShared.set(isClipping);
      },
      [columnClipping, columnKey],
    );

    // Auto-expand column width when content is clipping (triggered by double-tap)
    // TODO: Re-enable when auto-expand UX is refined
    // useEffect(() => {
    //   if (!beginUnclip) return;
    //
    //   let rafId: number;
    //   const tick = () => {
    //     if (clippingShared.get()) {
    //       columnWidths.value = { ...columnWidths.value, [columnKey]: (width.value += 15) };
    //       rafId = requestAnimationFrame(tick);
    //     } else {
    //       setBeginUnclip(false);
    //     }
    //   };
    //   rafId = requestAnimationFrame(tick);
    //
    //   return () => cancelAnimationFrame(rafId);
    // }, [beginUnclip, clippingShared, columnKey, columnWidths, width]);

    // useEffect(() => {
    //   const layout = measure(innerRef);
    //   columnPositionX.value = { ...columnPositionX.value, [columnKey]: layout?.pageX ?? 0 };
    // }, [columnKey, columnPositionX, innerRef]);

    // Double-tap to auto-expand (disabled - TODO: refine UX before re-enabling)
    // const doubleTap = useMemo(
    //   () =>
    //     Gesture.Tap()
    //       .numberOfTaps(2)
    //       .onStart(() => {
    //         "worklet";
    //         runOnJS(setBeginUnclip)(true);
    //       })
    //       .onEnd(() => {
    //         storage.saveFast(`${storageKey}-${columnKey}`, width.get().toString());
    //       }),
    //   [columnKey, storageKey, storage, width],
    // );

    const reorderDrag = useMemo(
      () =>
        Gesture.Pan()
          .activeOffsetX([-8, 8]) // Start after 8px horizontal movement
          .failOffsetY([-16, 16]) // Cancel if vertical movement exceeds threshold
          .onStart((event) => {
            "worklet";
            runOnJS(setIsDragging)(true);
            if (onDragStart) runOnJS(onDragStart)(columnKey, anchored, columnIndex, event.absoluteX);
          })
          .onChange((event) => {
            "worklet";
            if (onDragMove) runOnJS(onDragMove)(event.absoluteX);
          })
          .onEnd(() => {
            "worklet";
            runOnJS(setIsDragging)(false);
            if (onDragEnd) runOnJS(onDragEnd)();
          }),
      [columnKey, anchored, columnIndex, onDragStart, onDragMove, onDragEnd],
    );

    const drag = useMemo(
      () =>
        Gesture.Pan()
          .onStart(() => {
            "worklet";
            runOnJS(setResizerVisible)(true);
            if (onResizeStart) runOnJS(onResizeStart)(columnKey, width, anchored, columnIndex);
          })
          .onChange((event) => {
            "worklet";
            const current = width.get();
            const proposed = current + event.changeX;

            if (proposed >= minCellWidth) {
              // Only update header width during drag (cells don't re-render)
              width.set(proposed);
            }
          })
          .onEnd(() => {
            "worklet";
            runOnJS(setResizerVisible)(false);
            // Update all cell widths only on gesture end (single batch update)
            columnWidths.value = {
              ...columnWidths.value,
              [columnKey]: width.get(),
            };
            storage.saveFast(`${storageKey}-${columnKey}`, width.get());
            runOnJS(() => onResizeEnd?.())();
          }),
      [
        anchored,
        columnIndex,
        columnKey,
        columnWidths,
        minCellWidth,
        onResizeEnd,
        onResizeStart,
        storage,
        storageKey,
        width,
      ],
    );

    const containerStyle = useAnimatedStyle(() => {
      return {
        width: width.get(),
        minWidth: minCellWidth,
      };
    });

    // useAnimatedReaction(
    //   () => {
    //     return width.value;
    //   },
    //   (c, p) => {
    //     if (c !== p) {
    //       columnWidths.value[columnKey] = width.get();
    //     }
    //   },
    // );

    useEffect(() => {
      const layout = measure(innerRef);
      if (!layout) return;
      setMeasured({
        x: layout.pageX,
        y: layout.pageY,
        width: layout.width,
        height: layout.height,
      });
    }, [innerRef]);
    //
    // Determine if column can move left/right within its group
    const canMoveLeft = anchored ? relativeColumnIndex > 0 : relativeColumnIndex > 0;
    const canMoveRight = anchored ? relativeColumnIndex < anchoredCount - 1 : relativeColumnIndex < scrollableCount - 1;

    const handleMoveLeft = () => {
      contextMenu.setVisible(false);
      onMoveLeft?.(columnKey);
    };

    const handleMoveRight = () => {
      contextMenu.setVisible(false);
      onMoveRight?.(columnKey);
    };

    const handleSort = () => {
      if (!onOrderByChange) return;
      const isSortedOnThis = orderby?.name === columnKey;
      if (!isSortedOnThis) {
        onOrderByChange({ name: columnKey, direction: "ascending" });
      } else if (orderby.direction === "ascending") {
        onOrderByChange({ name: columnKey, direction: "descending" });
      } else {
        onOrderByChange(undefined);
      }
    };

    const sortIcon = (() => {
      if (orderby?.name !== columnKey) return "unfold-more-horizontal" as const;
      return orderby.direction === "ascending" ? ("chevron-up" as const) : ("chevron-down" as const);
    })();

    const menuContent = (
      <View style={styles.menu}>
        <Button
          enabled={!loading && canMoveLeft}
          iconPosition="right"
          style={styles.menuButton}
          text="Move Left"
          icon="table-arrow-left"
          onPress={handleMoveLeft}
        />
        <Button
          enabled={!loading && canMoveRight}
          iconPosition="right"
          style={styles.menuButton}
          text="Move Right"
          icon="table-arrow-right"
          onPress={handleMoveRight}
        />
        <Button
          enabled={!loading}
          iconPosition="right"
          style={styles.menuButton}
          text={anchored ? "Un-Pin to View" : "Pin-Column"}
          icon={anchored ? "table-column-remove" : "table-column-width"}
          onPress={toggleAnchor}
        />
        {onHideColumn && (
          <Button
            enabled={!loading}
            iconPosition="right"
            style={styles.menuButton}
            text="Hide Column"
            icon="eye-off"
            onPress={() => {
              contextMenu.setVisible(false);
              onHideColumn(columnKey);
            }}
          />
        )}
      </View>
    );
    // Just use drag gesture (doubleTap disabled)
    const composed = drag;

    return (
      <Animated.View
        ref={innerRef}
        onPointerDown={() => {
          const layout = measure(innerRef);
          if (!layout) return;
          setMeasured({ x: layout.pageX, y: layout.pageY, width: layout.width, height: layout.height });
        }}
        // onLayout={(e) => {
        // initialWidth ?? setInitialWidth(e.nativeEvent.layout.width);
        // }}
        style={[styles.container, { borderColor: colors.border }, style, containerStyle]}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        {...rest}
      >
        <GestureDetector gesture={reorderDrag}>
          <Button
            enabled={!loading}
            style={[
              { backgroundColor: colors.card, opacity: isDragging ? 1 : hovered ? 1 : 0.5 },
              styles.gripButton,
              //@ts-ignore
              { cursor: isDragging ? "grabbing" : "grab" },
            ]}
            hoverColor={colors.cardHover}
            iconSize={18}
            icon="drag-vertical"
            onPress={() => {}}
          />
        </GestureDetector>
        <ThemedText numberOfLines={1} type="defaultSemiBold" style={[styles.cellText, { marginLeft: 12 }]}>
          {text}
        </ThemedText>
        {onOrderByChange && !resizerVisible && (hovered || orderby?.name === columnKey) && (
          <Button
            enabled={!loading}
            style={[
              styles.sortButton,
              {
                position: "absolute",
                top: -5,
                left: "50%",
                transform: [{ translateX: "-50%" }],
                zIndex: 3,
                backgroundColor: colors.card,
                borderColor: colors.border,
              },
              orderby?.name === columnKey && { backgroundColor: colors.primary, borderColor: colors.primary },
            ]}
            hoverColor={orderby?.name === columnKey ? colors.primaryHover : colors.cardHover}
            iconSize={14}
            iconColor={orderby?.name === columnKey ? "#fff" : undefined}
            icon={sortIcon}
            onPress={handleSort}
          />
        )}
        <Button
          ref={buttonRef}
          enabled={!loading}
          style={[{ backgroundColor: colors.card }, styles.button]}
          hoverColor={colors.cardHover}
          iconSize={14}
          icon="dots-horizontal"
          onPress={() => {
            setMenuVisible((p) => {
              contextMenu.setVisible(!p);
              return !p;
            });
            contextMenu.setIgnoreRef(buttonRef);
            contextMenu.setLocation({ x: measured.x + measured.width - 200, y: measured.y + 38 });
            contextMenu.setMenuContent(menuContent);
          }}
        />
        <GestureDetector gesture={composed}>
          <Pressable
            style={[
              styles.resizer,
              { backgroundColor: colors.primary, opacity: resizerVisible ? 1 : hovered ? 0.5 : 0 },
              //@ts-ignore
              { cursor: "col-resize" },
            ]}
          />
        </GestureDetector>
      </Animated.View>
    );
  },
);

TableHeaderCell.displayName = "TableHeaderCell";

const styles = StyleSheet.create({
  container: {
    overflow: "visible",
    flexDirection: "row",
    height: "auto",
    borderBottomWidth: 0,
    borderTopWidth: 0,
    alignItems: "center",
    paddingHorizontal: 8,
  },
  button: {
    minHeight: 22,
    minWidth: 22,
    maxHeight: 22,
    maxWidth: 22,
    borderWidth: 0,
  },
  gripButton: {
    position: "absolute",
    left: 0,
    height: "100%",
    minWidth: 18,
    maxWidth: 18,
    borderWidth: 0,
    borderRadius: 0,
    zIndex: 2,
  },
  sortButton: {
    minHeight: 22,
    minWidth: 22,
    maxHeight: 22,
    maxWidth: 22,
    borderWidth: StyleSheet.hairlineWidth,
    zIndex: 4,
    elevation: 4,
  },
  menuButton: {
    justifyContent: "space-between",
    backgroundColor: "transparent",
    borderColor: "transparent",
  },
  resizer: {
    borderWidth: 0,
    width: 6,
    position: "absolute",
    right: 0,
    height: "100%",
  },
  menu: {
    minWidth: 180,
  },
  cellText: {
    flex: 1,
    alignContent: "center",
    paddingVertical: 4,
    justifyContent: "center",
  },
});
